import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { WrenchIcon, SparklesIcon, PaintbrushIcon, ShieldCheckIcon, BeakerIcon, DropletIcon, SprayCanIcon, StarIcon, PackageIcon, BoxIcon, CarIcon, FlaskConicalIcon, ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type ProductLinesProps = {
  language: Language;
  titleOverride?: string;
  variant?: 'static' | 'animated';
};
const categories = {
  ua: [{
    icon: WrenchIcon,
    name: 'Шпаклівка'
  }, {
    icon: SparklesIcon,
    name: 'Лаки'
  }, {
    icon: PaintbrushIcon,
    name: 'Грунти'
  }, {
    icon: ShieldCheckIcon,
    name: 'Захисні покриття'
  }, {
    icon: BeakerIcon,
    name: 'Ready Mix'
  }, {
    icon: FlaskConicalIcon,
    name: 'Розчинники та знежирювачі'
  }, {
    icon: SprayCanIcon,
    name: 'Аерозолі'
  }, {
    icon: StarIcon,
    name: 'Поліролі'
  }, {
    icon: DropletIcon,
    name: 'Клеї та герметики'
  }, {
    icon: BoxIcon,
    name: 'Витратні матеріали'
  }, {
    icon: ShieldCheckIcon,
    name: 'CHA Protect'
  }, {
    icon: CarIcon,
    name: 'Автокосметика'
  }],
  en: [{
    icon: WrenchIcon,
    name: 'Putty'
  }, {
    icon: SparklesIcon,
    name: 'Lacquers'
  }, {
    icon: PaintbrushIcon,
    name: 'Primers'
  }, {
    icon: ShieldCheckIcon,
    name: 'Protective Coatings'
  }, {
    icon: BeakerIcon,
    name: 'Ready Mix'
  }, {
    icon: FlaskConicalIcon,
    name: 'Solvents & Degreasers'
  }, {
    icon: SprayCanIcon,
    name: 'Aerosols'
  }, {
    icon: StarIcon,
    name: 'Polishes'
  }, {
    icon: DropletIcon,
    name: 'Adhesives & Sealants'
  }, {
    icon: BoxIcon,
    name: 'Consumables'
  }, {
    icon: ShieldCheckIcon,
    name: 'CHA Protect'
  }, {
    icon: CarIcon,
    name: 'Auto Cosmetics'
  }]
};
const sectionText = {
  ua: {
    title: 'Каталог продукції',
    subtitle: 'Професійні рішення для автомобільної індустрії',
    viewCategory: 'Переглянути'
  },
  en: {
    title: 'Product Catalog',
    subtitle: 'Professional solutions for automotive industry',
    viewCategory: 'View'
  }
};
export function ProductLines({
  language,
  titleOverride,
  variant = 'static'
}: ProductLinesProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.05
  });
  const t = sectionText[language];
  const categoryList = categories[language];
  return <section ref={ref} id="products" className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black" data-id="element-280">
      {/* Subtle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-281">
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-282" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-283">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 lg:mb-16" data-id="element-284">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4" data-id="element-285">
            {titleOverride || t.title}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-286">
            {t.subtitle}
          </p>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto mt-6" data-id="element-287" />
        </motion.div>

        {/* Categories Grid - 1 column on mobile for animated, 2 for others, 3 on desktop */}
        <div className={`grid ${variant === 'animated' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'} lg:grid-cols-3 gap-3 sm:gap-6`} data-id="element-288">
          {categoryList.map((category, index) => {
          const Icon = category.icon;
          return <motion.a key={category.name} href="#" initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} whileHover={{
            y: -8
          }} className="group block" data-id="element-289">
                {/* Card with unified style - matches Bestsellers */}
                <div className="glass-card rounded-xl sm:rounded-2xl overflow-hidden h-full transition-all duration-300 hover:border-chamaleon-yellow/30" data-id="element-290">
                  {/* Image area */}
                  <div className={`relative ${variant === 'animated' ? 'h-56 sm:h-64' : 'h-32 sm:h-56'} overflow-hidden flex items-center justify-center p-0 ${variant === 'animated' ? 'bg-gradient-to-br from-gray-100 to-gray-200' : 'bg-gradient-to-br from-chamaleon-charcoal to-chamaleon-black p-3 sm:p-4'}`} data-id="element-291">
                    {/* Static glow behind the can (only for static variant) */}
                    {variant === 'static' && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-32 sm:h-32 bg-black/40 rounded-full blur-xl pointer-events-none" data-id="element-292" />}

                    {/* Spinning Can Video (Animated Variant) */}
                    {variant === 'animated' ? <div className="relative w-full h-full flex items-center justify-center z-10 transition-transform duration-300 hover:scale-105" data-id="element-293">
                        <video src="https://cdn.magicpatterns.com/uploads/fvPTpQJgEHexT49TPLb4TN/20000-0120.mp4" autoPlay loop muted playsInline className="h-[120%] w-full object-cover" style={{
                    mixBlendMode: 'multiply'
                  }} data-id="element-294" />
                      </div> : <motion.div whileHover={{
                  scale: 1.1
                }} transition={{
                  duration: 0.3
                }} className="relative w-full h-full flex items-center justify-center z-10" data-id="element-295">
                        <img src="https://cdn.magicpatterns.com/uploads/vTnGo3ALhmp6MbgeXYfcPp/20000-0120.png" alt={category.name} className="h-full w-auto object-contain p-2 sm:p-0" data-id="element-296" />
                      </motion.div>}

                    {/* Hover glow (only for static variant since animated is light) */}
                    {variant === 'static' && <div className="absolute inset-0 bg-gradient-to-t from-chamaleon-yellow/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-297" />}
                  </div>

                  {/* Content */}
                  <div className="p-3 sm:p-6" data-id="element-298">
                    {/* Category name */}
                    <h3 className="text-sm sm:text-xl font-semibold text-white mb-2 sm:mb-4 transition-colors min-h-[2.5rem] sm:min-h-[3.5rem] flex items-center" data-id="element-299">
                      {category.name}
                    </h3>

                    {/* CTA */}
                    <motion.div className="inline-flex items-center gap-1 sm:gap-2 text-chamaleon-yellow font-medium text-xs sm:text-sm group/link" whileHover={{
                  x: 5
                }} data-id="element-300">
                      {t.viewCategory}
                      <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 group-hover/link:translate-x-1 transition-transform" data-id="element-301" />
                    </motion.div>
                  </div>
                </div>
              </motion.a>;
        })}
        </div>
      </div>
    </section>;
}